// Dictionary, 210 — the 210-specific segments with no shared equivalent: B3
// (Beginning Segment for Carrier's Invoice), C3 (Currency), G62 (Date/Time,
// whose G6201 date-qualifier list is its own two-character code set,
// distinct from — and never merged into — the shared DTM01 Date/Time
// Qualifier list used by every other transaction set), LX (Assigned
// Number, the stop loop's own counter), L5 (Description, Marks and
// Numbers), L0 (Line Item - Quantity and Weight), L1 (Rate and Charges)
// and L3 (Total Weight and Charges). Generic X12 210 (005010) definitions
// only; not tied to any trading partner's implementation guide. Sourced
// from
// reference/session/210-motor-carrier-freight-details-and-invoice-qualifiers-txt.md.
//
// REF, N1, N3, N4 and PER all recur inside the 210 too, but X12 defines one
// segment per id — they're already defined in dictionary/header-segments.ts
// and merged in by index.ts, so they are not redefined here. Their coded
// elements (REF01 Reference Identification Qualifier, N101 Entity
// Identifier Code) already carry the 210's codes, extended onto the shared
// dictionary modules in reference-identification-qualifiers.ts and
// entity-identifier-codes.ts.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {SHIPMENT_METHOD_OF_PAYMENT_CODES} from './shipment-method-of-payment-codes';
import {TRANSPORTATION_METHOD_CODES} from './transportation-method-codes';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

const DATE_QUALIFIER_CODES_210 = [
  {code: '86', label: 'Actual Pickup Date'},
  {code: '11', label: 'Shipped on This Date'},
  {code: '35', label: 'Delivered on This Date'},
  {code: '17', label: 'Estimated Delivery Date'},
  {code: '10', label: 'Requested Ship Date/Pick-up Date'},
  {code: '69', label: 'Scheduled Pick-Up Date'},
  {code: '70', label: 'Scheduled Delivery Date'},
  {code: '68', label: 'Requested Delivery Date'},
  {code: '22', label: 'Freight Bill Date'},
  {code: '03', label: 'Invoice Date'},
  {code: '76', label: 'Bill of Lading'},
  {code: 'ZZ', label: 'Mutually Defined'},
];

const WEIGHT_QUALIFIER_CODES_210 = [
  {code: 'G', label: 'Gross Weight'},
  {code: 'N', label: 'Actual Net Weight'},
  {code: 'B', label: 'Billed Weight'},
  {code: 'A1', label: 'Dimensional Weight'},
  {code: 'FR', label: 'Freight Weight'},
  {code: 'E', label: 'Estimated Net Weight'},
  {code: 'T', label: 'Tare Weight'},
  {code: 'ZZ', label: 'Mutually Defined'},
];

const WEIGHT_UNIT_CODE_210 = [
  {code: 'L', label: 'Pounds'},
  {code: 'K', label: 'Kilograms'},
  {code: 'G', label: 'Grams'},
];

const SPECIAL_CHARGE_ALLOWANCE_CODES_210 = [
  {code: '400', label: 'Freight'},
  {code: 'LHS', label: 'Linehaul Service'},
  {code: '405', label: 'Fuel Surcharge'},
  {code: 'FUE', label: 'Fuel Charge'},
  {code: 'DSC', label: 'Discount'},
  {
    code: 'LFT',
    label: 'Lift Gate (Truck) or Forklift Service at Pick-up/Delivery',
  },
  {code: 'RES', label: 'Residential Delivery'},
  {code: 'IDL', label: 'Inside Delivery'},
  {code: 'APT', label: 'Appointment (Notification)'},
  {code: 'DEL', label: 'Delivery Charge'},
  {code: 'PUC', label: 'Pick-up Charge'},
  {code: 'DET', label: 'Detention of Trailers'},
  {code: 'SOC', label: 'Stop-off Charge'},
  {code: 'RCL', label: 'Redelivery'},
  {code: 'HAZ', label: 'Hazardous Cargo Charge'},
  {code: 'OVR', label: 'Over Dimension'},
  {code: 'INS', label: 'Insurance'},
  {code: 'MIN', label: 'Minimum Guarantee'},
  {code: 'TAX', label: 'Tax Charge'},
  {code: 'GST', label: 'Goods and Services Tax Charge'},
  {code: 'MSC', label: 'Other Accessorial Service Charge'},
  {code: 'ZZ', label: 'Mutually Defined'},
];

export const SEGMENTS_210: Record<string, SegmentDefinition> = {
  B3: {
    id: 'B3',
    name: "Beginning Segment for Carrier's Invoice",
    purpose:
      "Identifies the carrier's invoice — its number, date and amount owed — along with how freight is paid for and how the shipment moved. Required at the start of every 210.",
    loop: LoopId.Header,
    required: true,
    elements: [
      el(
        1,
        'Shipment Qualifier',
        'Code distinguishing the type of shipment this invoice covers.'
      ),
      el(2, 'Invoice Number', "The carrier's invoice number.", {
        required: true,
      }),
      el(
        3,
        'Shipment Identification Number',
        'The shipment this invoice is for, in the carrier’s own numbering.'
      ),
      el(
        4,
        'Shipment Method of Payment',
        'Who pays the freight charges — prepaid, collect, third party, or another arrangement.',
        {
          dataType: 'code',
          required: true,
          codes: SHIPMENT_METHOD_OF_PAYMENT_CODES,
        }
      ),
      el(5, 'Weight Unit Code', 'The unit the shipment’s weight is given in.', {
        dataType: 'code',
        codes: WEIGHT_UNIT_CODE_210,
      }),
      el(6, 'Date', 'The invoice date.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
      el(7, 'Net Amount Due', 'The amount owed.', {
        dataType: 'numeric',
        required: true,
      }),
      el(
        10,
        'Transportation Method/Type Code',
        'How the shipment moved — motor common carrier, LTL, intermodal, and so on.',
        {
          dataType: 'code',
          codes: TRANSPORTATION_METHOD_CODES,
        }
      ),
    ],
  },

  C3: {
    id: 'C3',
    name: 'Currency',
    purpose:
      'States the currency the invoice’s monetary amounts are expressed in, and an exchange rate when one applies.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Currency Code', 'ISO currency code, e.g. USD or CAD.', {
        dataType: 'code',
        required: true,
      }),
      el(
        2,
        'Exchange Rate',
        'The rate used to convert to another currency, when one applies.',
        {dataType: 'numeric'}
      ),
    ],
  },

  G62: {
    id: 'G62',
    name: 'Date/Time',
    purpose:
      'Carries one of this 210’s dates — pickup, delivery, invoice, bill of lading, and the like — each tagged by its own date qualifier (G6201). The 210 uses G62 for every date instead of DTM; this qualifier list is two-character and entirely separate from the shared DTM01 Date/Time Qualifier list used by every other transaction set.',
    loop: LoopId.Header,
    elements: [
      el(1, 'Date Qualifier', 'Which kind of date this is.', {
        dataType: 'code',
        required: true,
        codes: DATE_QUALIFIER_CODES_210,
      }),
      el(2, 'Date', 'The date itself.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
    ],
  },

  LX: {
    id: 'LX',
    name: 'Assigned Number',
    purpose:
      'Opens one stop in this shipment’s itinerary and numbers it. Everything until the next LX (or the summary) — this stop’s parties, dates, description, weight and freight/accessorial charges — belongs to this stop.',
    loop: LoopId.Stop,
    required: true,
    elements: [
      el(
        1,
        'Assigned Number',
        'This stop’s sequence number within the shipment.',
        {
          dataType: 'numeric',
          required: true,
        }
      ),
    ],
  },

  L5: {
    id: 'L5',
    name: 'Description, Marks and Numbers',
    purpose: 'A free-form description of the lading handled at this stop.',
    loop: LoopId.Stop,
    elements: [
      el(
        1,
        'Lading Line Item Number',
        'Which lading line this description is for, when the stop has more than one.',
        {dataType: 'numeric'}
      ),
      el(2, 'Lading Description', 'Free-form description of the commodity.'),
    ],
  },

  L0: {
    id: 'L0',
    name: 'Line Item - Quantity and Weight',
    purpose:
      'The quantity and weight handled at this stop, with a weight qualifier saying what kind of weight is given (gross, net, billed, and so on).',
    loop: LoopId.Stop,
    elements: [
      el(
        1,
        'Lading Line Item Number',
        'Which lading line this quantity/weight is for, when the stop has more than one.',
        {dataType: 'numeric'}
      ),
      el(2, 'Lading Quantity', 'Number of pieces handled at this stop.', {
        dataType: 'numeric',
      }),
      el(4, 'Weight', 'The weight handled at this stop.', {
        dataType: 'numeric',
        required: true,
      }),
      el(5, 'Weight Qualifier', 'What kind of weight is given.', {
        dataType: 'code',
        required: true,
        codes: WEIGHT_QUALIFIER_CODES_210,
      }),
    ],
  },

  L1: {
    id: 'L1',
    name: 'Rate and Charges',
    purpose:
      'One freight or accessorial charge line for this stop — a special charge or allowance code and the amount charged for it.',
    loop: LoopId.FreightCharge,
    elements: [
      el(
        1,
        'Lading Line Item Number',
        'Which lading line this charge is for, when the stop has more than one.',
        {dataType: 'numeric'}
      ),
      el(4, 'Special Charges or Allowances', 'The amount charged for it.', {
        dataType: 'numeric',
        required: true,
      }),
      el(
        8,
        'Special Charge or Allowance Code',
        'What kind of freight or accessorial charge this line is.',
        {
          dataType: 'code',
          required: true,
          codes: SPECIAL_CHARGE_ALLOWANCE_CODES_210,
        }
      ),
    ],
  },

  L3: {
    id: 'L3',
    name: 'Total Weight and Charges',
    purpose:
      'The shipment’s total weight (with its qualifier) and the total amount charged across every stop’s freight and accessorial charges. Required at the end of every 210, before SE.',
    loop: LoopId.Summary,
    elements: [
      el(1, 'Weight', 'The shipment’s total weight.', {dataType: 'numeric'}),
      el(2, 'Weight Qualifier', 'What kind of weight is given.', {
        dataType: 'code',
        codes: WEIGHT_QUALIFIER_CODES_210,
      }),
      el(
        5,
        'Charge',
        'The total amount charged — expected to equal the sum of every L1 freight/accessorial charge line across every stop.',
        {dataType: 'numeric', required: true}
      ),
    ],
  },
};
